// src/contactsAdmin/index.js — 📇 Contacts admin (v129.42).
//
// Matt, 4 Aug: "yes build the admin contacts panel with account linking."
//
// v129.39 shipped the Contacts SCREEN and the `contacts` table but no way to
// maintain it: store phones were editable (Admin → Stores → the store modal),
// the Head Office directory was not. Editing a person meant SQL. That was my
// omission — "the numbers can be ammended in Admin" was in the original ask.
//
// ── ACCOUNT LINKING IS THE POINT ────────────────────────────────────────────
//
// `contacts.account_id` is what turns on 💬 Chat now and 📹 Call — without it a
// person's card shows a phone number and nothing else, and the per-person
// calling built in v129.41 has nobody to ring. The seed leaves all ~70 rows
// unlinked because a spreadsheet of landlines cannot know who holds which Shift
// login. So this panel's job is really: let a human make that call, 70 times,
// without writing SQL.
//
// It SUGGESTS a match (exact email, then exact name, then first name) and shows
// it as a one-tap button — never as a pre-selected dropdown value. A silent
// default that somebody saves without reading is how one manager's desk phone ends
// up ringing on another person's mobile, and a wrong link is worse than no link: the
// card would offer a Chat and a Call that reach the wrong person, which is the
// dead-action problem wearing a disguise.
//
// Writes are Head Office only (the Worker enforces it; this hides the controls
// so an AM is not offered a button that 403s).

function api(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}
function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function toast(msg, kind) {
  try { if (typeof showToast === 'function') showToast(msg, kind); } catch (e) { /* no toast host */ }
}

var _rows = null;      // contacts, or null = not loaded / read failed
var _err = null;
var _q = '';
var _editing = null;   // the row being edited, or {} for a new one

// Head Office only, same rule the Worker applies to POST/PATCH.
function canEdit() {
  try { return String((currentAdmin && currentAdmin.role) || '').toLowerCase() === 'admin'; } catch (e) { return false; }
}

// The live account list the admin app already keeps in localStorage
// (src/accounts/index.js). Read through the shared loader rather than the cache
// key so there is one definition of "the accounts".
function accounts() {
  try {
    var list = (typeof loadAccounts === 'function') ? loadAccounts() : [];
    return (Array.isArray(list) ? list : []).filter(function (a) { return a && a.id != null && a.active !== false; });
  } catch (e) { return []; }
}
function accountById(id) {
  if (id == null) return null;
  var hit = accounts().filter(function (a) { return String(a.id) === String(id); });
  return hit[0] || null;
}

// ── the suggestion ──────────────────────────────────────────────────────────
// Three passes, strongest first. Returns an account or null — never a guess it
// cannot justify, because the whole value of the suggestion is that the reader
// can see WHY in one glance.
export function suggestAccount(contact, list) {
  var people = Array.isArray(list) ? list : accounts();
  if (!contact) return null;
  var email = String(contact.email || '').trim().toLowerCase();
  var name = String(contact.name || '').trim().toLowerCase();
  if (email) {
    var byEmail = people.filter(function (a) { return String(a.email || '').trim().toLowerCase() === email; });
    if (byEmail.length === 1) return { account: byEmail[0], why: 'same email address' };
  }
  if (!name) return null;
  var byName = people.filter(function (a) { return String(a.name || '').trim().toLowerCase() === name; });
  if (byName.length === 1) return { account: byName[0], why: 'same name' };
  // The sheet is first names in capitals ('ALEX'); accounts are full names.
  // Only accept it when exactly ONE account starts with that first name —
  // two Ahmeds must be picked by a human, not by whichever sorted first.
  var first = name.split(/\s+/)[0];
  if (first.length < 3) return null;
  var byFirst = people.filter(function (a) {
    return String(a.name || '').trim().toLowerCase().split(/\s+/)[0] === first;
  });
  if (byFirst.length === 1) return { account: byFirst[0], why: 'first name matches' };
  return null;
}

// ── load ────────────────────────────────────────────────────────────────────
export async function load(force) {
  if (_rows && !force) return;
  try {
    var res = await api('/api/admin/contacts');
    var d = await res.json().catch(function () { return {}; });
    if (res.ok && d && d.ok) { _rows = Array.isArray(d.contacts) ? d.contacts : []; _err = null; }
    else { _rows = null; _err = (d && d.error) || ('HTTP ' + res.status); }
  } catch (e) {
    _rows = null; _err = (e && e.message) || 'check the connection';
  }
  render();
}

// ── render ──────────────────────────────────────────────────────────────────
function matches(r, q) {
  var s = String(q || '').trim().toLowerCase();
  if (!s) return true;
  var hay = [r.name, r.department, r.job_title, r.email, r.extension, r.mobile]
    .filter(Boolean).join(' ').toLowerCase();
  var digits = s.replace(/\D/g, '');
  if (digits.length >= 3 && hay.replace(/\D/g, '').indexOf(digits) !== -1) return true;
  return hay.indexOf(s) !== -1;
}

function linkCell(r) {
  var acc = accountById(r.account_id);
  if (acc) {
    return '<span class="badge badge-green" style="font-size:11px" translate="no">🔗 ' + esc(acc.name || ('#' + acc.id)) + '</span>';
  }
  if (r.account_id != null) {
    // Pointed at an account that no longer exists or is switched off. Say so —
    // the staff screen already refuses to render Chat/Call for it, and silently
    // showing "not linked" would hide a row that needs fixing.
    return '<span class="badge badge-grey" style="font-size:11px" title="account ' + esc(r.account_id) + ' is inactive or deleted">⚠️ dead link</span>';
  }
  var s = suggestAccount(r);
  if (s && canEdit()) {
    return '<button class="btn btn-outline" style="font-size:11px;padding:3px 8px" ' +
      'onclick="ContactsAdmin.linkNow(' + Number(r.id) + ',' + Number(s.account.id) + ')" ' +
      'title="' + esc(s.why) + '">🔗 Link ' + esc(s.account.name || '') + '?</button>';
  }
  return '<span style="font-size:11px;color:var(--sub)">no login</span>';
}

export function render() {
  var el = document.getElementById('storeContactsBody');
  if (!el) return;
  if (_rows === null && _err === null) { el.innerHTML = rowMsg('Loading contacts…'); load(); return; }

  if (_rows === null) {
    var isMissing = /v184|not found|503/i.test(String(_err));
    el.innerHTML = rowMsg(isMissing
      ? 'The contacts table is not there yet — run migrations/v184-0-contacts.sql, then docs/sql/09-contacts-seed.sql.'
      : ('Could not load contacts — ' + _err), true);
    return;
  }

  var shown = _rows.filter(function (r) { return matches(r, _q); });
  var linked = _rows.filter(function (r) { return r.account_id != null && accountById(r.account_id); }).length;
  var missing = missingAccounts(_rows, accounts()).length;

  var head = '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:10px">' +
    '<input id="contactsAdminSearch" type="search" value="' + esc(_q) + '" placeholder="🔍 name, department or number" ' +
      'oninput="ContactsAdmin.search(this.value)" style="flex:1;min-width:200px;padding:8px 11px;border:1.5px solid var(--border);border-radius:9px;font-family:inherit;font-size:13px">' +
    (canEdit() ? '<button class="btn btn-green" onclick="ContactsAdmin.openModal()">+ Add Contact</button>' : '') +
    // v129.44 — only offered when there is actually somebody to add. A button
    // reading "Add 0" is a button that has to be tapped to learn it does
    // nothing, which is the dead-action rule this whole module is built on.
    ((canEdit() && missing) ? '<button class="btn btn-outline" onclick="ContactsAdmin.openAddMissing()">➕ Add ' + missing + ' from Shift logins</button>' : '') +
    '</div>' +
    '<div style="font-size:12px;color:var(--sub);margin-bottom:12px;line-height:1.6">' +
      '<b>' + _rows.length + '</b> contacts · <b>' + linked + '</b> linked to a Shift login. ' +
      'Linking is what switches on 💬 Chat and 📹 Call on their card — without it, staff get their phone number and email only.' +
      // Says WHY somebody is missing before they go looking for a bug: the
      // seed is the landline sheet, so a person with a login but no desk
      // extension was never in it.
      (missing ? ' <b>' + missing + '</b> people have a Shift login but no entry here — the directory was seeded from the landline sheet, which only lists desks with an extension.' : '') +
      (canEdit() ? '' : ' <b>Head Office can edit this list.</b>') +
    '</div>';

  var rows = shown.length ? shown.map(function (r) {
    return '<tr' + (r.active === false ? ' style="opacity:.5"' : '') + '>' +
      '<td><strong translate="no">' + esc(r.name || '') + '</strong>' +
        (r.active === false ? ' <span class="badge badge-grey" style="font-size:10px">hidden</span>' : '') + '</td>' +
      '<td style="font-size:12px;color:var(--sub)" translate="no">' + esc(r.department || '') + '</td>' +
      '<td style="font-size:12px" translate="no">' + esc(r.extension || '') + '</td>' +
      '<td style="font-size:12px" translate="no">' + esc(r.mobile || '') + '</td>' +
      '<td style="font-size:12px;color:var(--sub)" translate="no">' + esc(r.email || '') + '</td>' +
      '<td>' + linkCell(r) + '</td>' +
      '<td style="text-align:end">' +
        (canEdit() ? '<button class="btn btn-outline" style="font-size:11px;padding:3px 9px" onclick="ContactsAdmin.openModal(' + Number(r.id) + ')">Edit</button>' : '') +
      '</td></tr>';
  }).join('') : rowMsg(_q ? 'Nobody matches that search.' : 'No contacts yet — run docs/sql/09-contacts-seed.sql, or add them here.');

  el.innerHTML = head +
    '<div class="card table-wrap" style="padding:0"><table class="data-table">' +
    '<thead><tr><th>Name</th><th>Department</th><th>Ext</th><th>Mobile</th><th>Email</th><th>Shift login</th><th></th></tr></thead>' +
    '<tbody>' + rows + '</tbody></table></div>';
}

function rowMsg(msg, bad) {
  return '<div style="padding:18px;text-align:center;font-size:12.5px;color:' +
    (bad ? 'var(--arota-red-ink)' : 'var(--sub)') + '">' + esc(msg) + '</div>';
}

export function search(v) { _q = String(v || ''); render(); }

// ── v129.44 — who the landline sheet never had ──────────────────────────────
//
// Matt, 4 Aug: "have all the phone numbers gone in? Says 70 but doesnt feel
// like that. Cant find people like [three Head Office colleagues]… people that are
// already set up on the app?"
//
// They went in correctly — and they are not there. The seed is the LANDLINE
// SHEET, which lists Head Office DESKS: an extension, and whoever sits at it.
// Of its 70 rows, three are SPARE and six are CONF-1, CONF-2, SECURITY, PANTRY
// and two fax machines, so it is really ~62 people — which is why 70 does not
// feel like 70. Anyone with a Shift login but no desk extension on that sheet
// was never in it to begin with.
//
// So: find every ACTIVE account with no contact row and offer to add them,
// PRE-LINKED, which also does their 💬 Chat / 📹 Call wiring in the same tap.
//
// Deliberately a PREVIEW with tick boxes rather than a silent bulk insert. The
// name matching below is a heuristic, and a heuristic that writes rows without
// showing you them first is how you end up with MOHD and Mohammed Ali as two
// people in the company directory.
export function missingAccounts(contacts, accounts) {
  const rows = Array.isArray(contacts) ? contacts : [];
  const people = Array.isArray(accounts) ? accounts : [];
  const linked = {};
  const byEmail = {};
  const byName = {};
  const byFirst = {};
  rows.forEach((c) => {
    if (!c) return;
    if (c.account_id != null) linked[String(c.account_id)] = 1;
    const em = String(c.email || '').trim().toLowerCase();
    if (em) byEmail[em] = 1;
    const nm = String(c.name || '').trim().toLowerCase();
    if (nm) { byName[nm] = 1; byFirst[nm.split(/\s+/)[0]] = 1; }
  });
  return people.filter((a) => {
    if (!a || a.id == null || a.active === false) return false;
    if (linked[String(a.id)]) return false;
    const em = String(a.email || '').trim().toLowerCase();
    if (em && byEmail[em]) return false;
    const nm = String(a.name || '').trim().toLowerCase();
    if (!nm) return false;
    if (byName[nm]) return false;
    // The sheet is first names in capitals ('ALEX'); accounts are full
    // names. Treat a first-name hit as "already in the book" so the obvious
    // duplicate never gets offered — the cost of being wrong here is a person
    // missing from the list, which the reader can see and add by hand, versus
    // a duplicate they cannot see is a duplicate.
    return !byFirst[nm.split(/\s+/)[0]];
  });
}

var _addPreview = null;   // the accounts currently ticked in the add-missing modal

export function openAddMissing() {
  if (!canEdit()) { toast('Head Office can edit contacts', 'error'); return; }
  const missing = missingAccounts(_rows || [], accounts());
  const host = document.getElementById('contactsAdminModal');
  if (!host) return;
  if (!missing.length) {
    toast('Every Shift login already has a contact', 'success');
    return;
  }
  _addPreview = missing;
  host.innerHTML = '<div style="background:var(--card);border-radius:16px;padding:20px;max-width:460px;width:92%;max-height:88vh;overflow:auto">' +
    '<div style="font-weight:900;font-size:16px;margin-bottom:6px">Add ' + missing.length + ' account holder' + (missing.length === 1 ? '' : 's') + '</div>' +
    '<div style="font-size:12.5px;color:var(--sub);line-height:1.55;margin-bottom:12px">' +
      'These people have a Shift login but no entry in the phone book — the directory was seeded from the ' +
      'landline sheet, which only lists desks with an extension. Adding them here links each one to their ' +
      'login, so 💬 Chat and 📹 Call work straight away. Numbers can be filled in afterwards.' +
    '</div>' +
    '<div style="border:1px solid var(--border);border-radius:10px;padding:6px 10px;margin-bottom:12px;max-height:280px;overflow:auto">' +
    missing.map(function (a, i) {
      return '<label style="display:flex;align-items:center;gap:8px;padding:5px 0;font-size:13px;cursor:pointer">' +
        '<input type="checkbox" class="cx-add-chk" data-i="' + i + '" checked style="width:15px;height:15px;flex:none">' +
        '<span style="flex:1;min-width:0" translate="no"><b>' + esc(a.name || ('#' + a.id)) + '</b>' +
        (a.department ? ' <span style="color:var(--sub)">· ' + esc(a.department) + '</span>' : '') +
        (a.email ? '<div style="font-size:11.5px;color:var(--sub)">' + esc(a.email) + '</div>' : '') +
        '</span></label>';
    }).join('') +
    '</div>' +
    '<div style="display:flex;gap:8px;justify-content:flex-end">' +
      '<button class="btn btn-outline" onclick="ContactsAdmin.closeModal()">Cancel</button>' +
      '<button class="btn btn-green" onclick="ContactsAdmin.saveAddMissing()">Add ticked</button>' +
    '</div></div>';
  host.style.display = 'flex';
}

export async function saveAddMissing() {
  if (!canEdit() || !Array.isArray(_addPreview)) return;
  const picked = [];
  const nodes = document.querySelectorAll('#contactsAdminModal .cx-add-chk');
  Array.prototype.forEach.call(nodes, function (chk) {
    if (!chk.checked) return;
    const a = _addPreview[Number(chk.getAttribute('data-i'))];
    if (a) {
      picked.push({
        name: a.name || ('Account ' + a.id),
        department: a.department || null,
        email: a.email || null,
        account_id: Number(a.id),
        active: true,
      });
    }
  });
  if (!picked.length) { toast('Nothing ticked', 'error'); return; }
  try {
    const res = await api('/api/admin/contacts', { method: 'POST', body: JSON.stringify({ rows: picked }) });
    const d = await res.json().catch(function () { return {}; });
    if (!res.ok || !d.ok) { toast((d && d.error) || 'Could not add those contacts', 'error'); return; }
    closeModal();
    _addPreview = null;
    toast('Added ' + picked.length + ' contact' + (picked.length === 1 ? '' : 's'), 'success');
    await load(true);
  } catch (e) {
    toast('Could not add — check the connection', 'error');
  }
}

// ── the modal ───────────────────────────────────────────────────────────────
export function openModal(id) {
  if (!canEdit()) { toast('Head Office can edit contacts', 'error'); return; }
  var row = (id != null && _rows) ? _rows.filter(function (r) { return String(r.id) === String(id); })[0] : null;
  _editing = row ? Object.assign({}, row) : {};
  var host = document.getElementById('contactsAdminModal');
  if (!host) return;
  host.innerHTML = modalHtml(_editing);
  host.style.display = 'flex';
  var f = document.getElementById('cxName');
  if (f) { try { f.focus(); } catch (e) { /* not focusable yet */ } }
}

export function closeModal() {
  _editing = null;
  var host = document.getElementById('contactsAdminModal');
  if (host) { host.style.display = 'none'; host.innerHTML = ''; }
}

function field(id, label, value, placeholder) {
  return '<label style="display:block;margin-bottom:9px">' +
    '<span style="display:block;font-size:11.5px;font-weight:700;color:var(--sub);margin-bottom:3px">' + esc(label) + '</span>' +
    '<input id="' + id + '" value="' + esc(value == null ? '' : value) + '" placeholder="' + esc(placeholder || '') + '" ' +
    'style="width:100%;box-sizing:border-box;padding:9px 11px;border:1.5px solid var(--border);border-radius:9px;font-family:inherit;font-size:13.5px"></label>';
}

function modalHtml(r) {
  var list = accounts().slice().sort(function (a, b) { return String(a.name || '').localeCompare(String(b.name || '')); });
  var sug = (r.account_id == null) ? suggestAccount(r, list) : null;
  var opts = '<option value="">— not linked (phone + email only) —</option>' +
    list.map(function (a) {
      return '<option value="' + Number(a.id) + '"' + (String(a.id) === String(r.account_id) ? ' selected' : '') + '>' +
        esc(a.name || ('#' + a.id)) + (a.email ? ' · ' + esc(a.email) : '') + '</option>';
    }).join('');

  return '<div style="background:var(--card);border-radius:16px;padding:20px;max-width:460px;width:92%;max-height:88vh;overflow:auto">' +
    '<div style="font-weight:900;font-size:16px;margin-bottom:12px">' + (r.id ? 'Edit contact' : 'Add contact') + '</div>' +
    field('cxName', 'Name', r.name, 'ALEX') +
    field('cxDept', 'Department', r.department, 'GEN. MANAGER') +
    field('cxTitle', 'Job title', r.job_title, '') +
    field('cxExt', 'Extension', r.extension, '2476730') +
    field('cxMobile', 'Company mobile', r.mobile, '055-6086615') +
    field('cxEmail', 'Email', r.email, 'name@algt-me.com') +
    '<label style="display:block;margin-bottom:4px">' +
      '<span style="display:block;font-size:11.5px;font-weight:700;color:var(--sub);margin-bottom:3px">Shift login</span>' +
      '<select id="cxAccount" style="width:100%;box-sizing:border-box;padding:9px 11px;border:1.5px solid var(--border);border-radius:9px;font-family:inherit;font-size:13.5px">' +
      opts + '</select></label>' +
    // The suggestion is a BUTTON, never a pre-selected value. Somebody saving
    // without reading must not silently point Chat and Call at the wrong person.
    (sug ? '<div style="font-size:12px;color:var(--info-deep);background:var(--info-bg);border:1px solid var(--arota-info-border);border-radius:9px;padding:8px 10px;margin-bottom:8px">' +
      'Looks like <b translate="no">' + esc(sug.account.name || '') + '</b> (' + esc(sug.why) + ') — ' +
      '<a href="#" onclick="ContactsAdmin.useSuggestion(' + Number(sug.account.id) + ');return false" style="font-weight:800">use this</a>' +
      '</div>' : '') +
    '<div style="font-size:11.5px;color:var(--sub);line-height:1.55;margin-bottom:12px">' +
      'Linking turns on 💬 Chat and 📹 Call for this person. Leave it unlinked for anyone without a Shift login — their phone and email still show.' +
    '</div>' +
    '<label style="display:flex;align-items:center;gap:7px;font-size:13px;margin-bottom:14px;cursor:pointer">' +
      '<input type="checkbox" id="cxActive"' + (r.active === false ? '' : ' checked') + ' style="width:15px;height:15px">' +
      '<span>Show in Contacts</span></label>' +
    '<div style="display:flex;gap:8px;justify-content:flex-end">' +
      '<button class="btn btn-outline" onclick="ContactsAdmin.closeModal()">Cancel</button>' +
      '<button class="btn btn-green" onclick="ContactsAdmin.save()">Save</button>' +
    '</div></div>';
}

export function useSuggestion(accountId) {
  var sel = document.getElementById('cxAccount');
  if (sel) sel.value = String(accountId);
}

function val(id) {
  var el = document.getElementById(id);
  return el ? String(el.value || '').trim() : '';
}

export async function save() {
  var name = val('cxName');
  if (!name) { toast('A contact needs a name', 'error'); return; }
  var sel = document.getElementById('cxAccount');
  var accRaw = sel ? String(sel.value || '') : '';
  var body = {
    name: name,
    department: val('cxDept') || null,
    job_title: val('cxTitle') || null,
    extension: val('cxExt') || null,
    mobile: val('cxMobile') || null,
    email: val('cxEmail') || null,
    // '' means "not linked" — send null, not 0, or PostgREST writes a link to
    // account 0 and the card offers a Chat that goes nowhere.
    account_id: accRaw ? Number(accRaw) : null,
    active: !!(document.getElementById('cxActive') || {}).checked,
  };
  var id = _editing && _editing.id;
  try {
    var res = await api('/api/admin/contacts' + (id ? '?id=' + Number(id) : ''), {
      method: id ? 'PATCH' : 'POST', body: JSON.stringify(body),
    });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || !d.ok) { toast((d && d.error) || 'Could not save that contact', 'error'); return; }
    closeModal();
    toast('Contact saved', 'success');
    await load(true);
  } catch (e) {
    toast('Could not save — check the connection', 'error');
  }
}

// One-tap accept of a suggestion straight from the table, so 70 rows do not
// mean 70 modals.
export async function linkNow(contactId, accountId) {
  if (!canEdit()) return;
  var acc = accountById(accountId);
  if (!acc) { toast('That account is gone — reload and try again', 'error'); return; }
  try {
    var res = await api('/api/admin/contacts?id=' + Number(contactId), {
      method: 'PATCH', body: JSON.stringify({ account_id: Number(accountId) }),
    });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || !d.ok) { toast((d && d.error) || 'Could not link that contact', 'error'); return; }
    toast('Linked to ' + (acc.name || ('#' + accountId)), 'success');
    await load(true);
  } catch (e) {
    toast('Could not link — check the connection', 'error');
  }
}

export { canEdit };
